import { Button } from "@etudescarre/ui";
import { isNotNil, validatePasswordStrength } from "@etudescarre/utils";
import { CheckCircleIcon, EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";
import { authClient } from "@/lib/auth-client";
import { PasswordCriteria } from "./password-criteria";

type FormData = {
  password: string;
  confirmPassword: string;
};

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const token = searchParams.get("token");

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: { password: "", confirmPassword: "" },
  });

  const password = watch("password");

  const handleSubmitForm = async (data: FormData) => {
    if (!token) {
      setError(
        "Lien invalide. Veuillez recommencer depuis la page de connexion.",
      );
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      const result = await authClient.resetPassword({
        newPassword: data.password,
        token,
        fetchOptions: { throw: false },
      });

      if (result.error) {
        setError(
          "Ce lien est invalide ou a expiré. Veuillez recommencer depuis la page de connexion.",
        );
        return;
      }

      setSuccess(true);
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  const layout = (children: React.ReactNode) => (
    <div className="flex min-h-screen">
      <div
        className="hidden w-2/3 bg-cover bg-center lg:block"
        style={{ backgroundImage: "url(/login-bg.jpg)" }}
      >
        <div className="flex h-full items-end p-8">
          <p className="text-white text-sm">© ChantierCarré</p>
        </div>
      </div>
      <div className="flex w-full items-center justify-center bg-white px-8 lg:w-1/3">
        <div className="w-full max-w-md space-y-8">
          <div className="mb-8">
            <img src="/logo.png" alt="ChantierCarré" className="h-12" />
          </div>
          {children}
        </div>
      </div>
    </div>
  );

  if (!token) {
    return layout(
      <div className="space-y-4">
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-red-800 text-sm">
            Lien invalide. Veuillez recommencer depuis la page de connexion.
          </p>
        </div>
        <Button
          className="w-full bg-[#1a3b5c] hover:bg-[#2a4b6c]"
          onClick={() => navigate("/auth/login")}
          type="button"
        >
          Retour à la connexion
        </Button>
      </div>,
    );
  }

  if (success) {
    return layout(
      <div className="space-y-6 text-center">
        <CheckCircleIcon className="mx-auto h-14 w-14 text-green-500" />
        <div className="space-y-2">
          <h2 className="font-semibold text-xl text-gray-900">
            Mot de passe modifié !
          </h2>
          <p className="text-gray-600 text-sm">
            Votre mot de passe a correctement été changé.
          </p>
          <p className="text-gray-600 text-sm">
            Vous pouvez désormais vous connecter à votre compte.
          </p>
        </div>
        <Button
          className="w-full bg-[#1a3b5c] hover:bg-[#2a4b6c]"
          onClick={() => navigate("/auth/login")}
          type="button"
        >
          Se connecter
        </Button>
      </div>,
    );
  }

  return layout(
    <>
      <div>
        <p className="text-gray-600">Créer un nouveau mot de passe</p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit(handleSubmitForm)}>
        <div>
          <label
            className="mb-2 block font-medium text-gray-700 text-sm uppercase"
            htmlFor="password"
          >
            Nouveau mot de passe
          </label>
          <div className="relative">
            <input
              {...register("password", {
                validate: (val) => validatePasswordStrength(val).length === 0,
              })}
              aria-describedby="password-criteria"
              aria-invalid={isNotNil(errors.password)}
              autoComplete="new-password"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 text-gray-900 placeholder-gray-400 focus:border-[#1a3b5c] focus:outline-none focus:ring-1 focus:ring-[#1a3b5c]"
              id="password"
              placeholder="Votre nouveau mot de passe"
              type={showPassword ? "text" : "password"}
            />
            <button
              aria-label={
                showPassword
                  ? "Masquer le mot de passe"
                  : "Afficher le mot de passe"
              }
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setShowPassword(!showPassword)}
              type="button"
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <EyeIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>
          </div>

          <PasswordCriteria
            id="password-criteria"
            password={password}
            showErrors={isNotNil(errors.password)}
          />
        </div>

        <div>
          <label
            className="mb-2 block font-medium text-gray-700 text-sm uppercase"
            htmlFor="confirm-password"
          >
            Confirmer le mot de passe
          </label>
          <div className="relative">
            <input
              {...register("confirmPassword", {
                validate: (val) =>
                  val === getValues("password") ||
                  "Les mots de passe ne correspondent pas.",
              })}
              autoComplete="new-password"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 text-gray-900 placeholder-gray-400 focus:border-[#1a3b5c] focus:outline-none focus:ring-1 focus:ring-[#1a3b5c]"
              id="confirm-password"
              placeholder="Confirmez votre mot de passe"
              type={showConfirmPassword ? "text" : "password"}
            />
            <button
              aria-label={
                showConfirmPassword
                  ? "Masquer le mot de passe"
                  : "Afficher le mot de passe"
              }
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              type="button"
            >
              {showConfirmPassword ? (
                <EyeSlashIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <EyeIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-red-600 text-sm">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <Button
          className="w-full bg-[#1a3b5c] hover:bg-[#2a4b6c]"
          disabled={isLoading}
          type="submit"
        >
          {isLoading ? "Enregistrement..." : "Valider"}
        </Button>
      </form>
    </>,
  );
}
